import { useState, useEffect } from 'react';
import { Card, Button, Input, NumberInput, Badge } from '@atoms/index';
import { ZoneDrawingMap, ConfirmModal } from '@organisms/index';
import { apiClient } from '@services/api';
import { useRestaurantStore } from '@store/restaurantStore';
import { GeoJsonPolygon } from '@/utils/geojson';
import { Toast } from '@atoms/index';

interface RestaurantZone {
  id: string;
  name: string;
  deliveryFee: number;
  minimumOrderAmount: number;
  estimatedDeliveryMinutes: number;
  priority: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const DeliveryZonesTab = () => {
  const { restaurant } = useRestaurantStore();
  const [zones, setZones] = useState<RestaurantZone[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingZone, setEditingZone] = useState<RestaurantZone | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    deliveryFee: 0,
    minimumOrderAmount: 0,
    estimatedDeliveryMinutes: 30,
    priority: 0,
  });

  const [polygon, setPolygon] = useState<GeoJsonPolygon | null>(null);
  const [isPolygonValid, setIsPolygonValid] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteZoneId, setDeleteZoneId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (restaurant?.id) {
      loadZones();
    }
  }, [restaurant?.id]);

  const loadZones = async () => {
    if (!restaurant?.id) return;

    setIsLoading(true);
    try {
      const response = await apiClient.get(`/catalog/restaurants/${restaurant.id}/zones`);
      const allZones = response.data.data.zones || [];
      const activeZones = allZones.filter((zone: RestaurantZone) => zone.isActive !== false);
      setZones(activeZones);
    } catch (error) {
      console.error('Failed to load zones:', error);
      setToast({ message: 'Bölgeler yüklenirken bir hata oluştu', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateZone = async () => {
    if (!restaurant?.id || !polygon || !isPolygonValid) {
      setToast({ message: 'Lütfen geçerli bir polygon çizin', type: 'error' });
      return;
    }

    if (!formData.estimatedDeliveryMinutes || formData.estimatedDeliveryMinutes < 1) {
      setToast({ message: 'Lütfen tahmini teslimat süresini girin (en az 1 dakika)', type: 'error' });
      return;
    }

    setIsCreating(true);
    try {
      console.log('[DeliveryZonesTab] Sending zone data:', {
        name: formData.name,
        boundary: polygon,
        boundaryType: typeof polygon,
        boundaryStringified: JSON.stringify(polygon),
      });

      await apiClient.post(`/catalog/restaurants/${restaurant.id}/zones`, {
        name: formData.name,
        boundary: polygon,
        deliveryFee: formData.deliveryFee,
        minimumOrderAmount: formData.minimumOrderAmount,
        estimatedDeliveryMinutes: formData.estimatedDeliveryMinutes,
        priority: formData.priority,
      });

      setToast({ message: 'Bölge başarıyla oluşturuldu', type: 'success' });
      setShowCreateForm(false);
      setFormData({
        name: '',
        deliveryFee: 0,
        minimumOrderAmount: 0,
        estimatedDeliveryMinutes: 30,
        priority: 0,
      });
      setPolygon(null);
      loadZones();
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Bölge oluşturulurken bir hata oluştu';
      setToast({ message, type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteZone = async () => {
    if (!restaurant?.id || !deleteZoneId) return;

    setIsDeleting(true);
    try {
      await apiClient.delete(`/catalog/restaurants/${restaurant.id}/zones/${deleteZoneId}`);
      setToast({ message: 'Bölge başarıyla silindi', type: 'success' });
      setDeleteZoneId(null);
      loadZones();
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Bölge silinirken bir hata oluştu';
      setToast({ message, type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditZone = async (zone: RestaurantZone) => {
    setEditingZone(zone);
    setFormData({
      name: zone.name,
      deliveryFee: zone.deliveryFee,
      minimumOrderAmount: zone.minimumOrderAmount,
      estimatedDeliveryMinutes: zone.estimatedDeliveryMinutes,
      priority: zone.priority,
    });
    setShowCreateForm(true);
    setPolygon(null);
    setIsPolygonValid(false);
    setValidationError(null);

    try {
      console.log('[DeliveryZonesTab] Loading zone boundary for edit:', zone.id);
      const response = await apiClient.get(`/catalog/restaurants/${restaurant?.id}/zones/${zone.id}`);
      console.log('[DeliveryZonesTab] Zone boundary response:', response.data);
      if (response.data.data.boundary) {
        const boundary = response.data.data.boundary;
        console.log('[DeliveryZonesTab] Setting polygon:', boundary);
        setPolygon(boundary);
        setIsPolygonValid(true);
      } else {
        console.warn('[DeliveryZonesTab] No boundary in response');
      }
    } catch (error) {
      console.error('Failed to load zone boundary:', error);
    }
  };

  const handleUpdateZone = async () => {
    if (!restaurant?.id || !editingZone || !polygon || !isPolygonValid) {
      setToast({ message: 'Lütfen geçerli bir polygon çizin', type: 'error' });
      return;
    }

    if (!formData.estimatedDeliveryMinutes || formData.estimatedDeliveryMinutes < 1) {
      setToast({ message: 'Lütfen tahmini teslimat süresini girin (en az 1 dakika)', type: 'error' });
      return;
    }

    setIsUpdating(true);
    try {
      console.log('[DeliveryZonesTab] Updating zone:', {
        zoneId: editingZone.id,
        boundary: polygon,
        boundaryStringified: JSON.stringify(polygon),
        formData,
      });

      const response = await apiClient.patch(`/catalog/restaurants/${restaurant.id}/zones/${editingZone.id}`, {
        name: formData.name,
        boundary: polygon,
        deliveryFee: formData.deliveryFee,
        minimumOrderAmount: formData.minimumOrderAmount,
        estimatedDeliveryMinutes: formData.estimatedDeliveryMinutes,
        priority: formData.priority,
      });

      console.log('[DeliveryZonesTab] Update response:', response.data);

      setToast({ message: 'Bölge başarıyla güncellendi', type: 'success' });
      setShowCreateForm(false);
      setEditingZone(null);
      setFormData({
        name: '',
        deliveryFee: 0,
        minimumOrderAmount: 0,
        estimatedDeliveryMinutes: 30,
        priority: 0,
      });
      setPolygon(null);
      setIsPolygonValid(false);
      setValidationError(null);
      loadZones();
    } catch (error: any) {
      console.error('[DeliveryZonesTab] Update error:', error);
      const message = error.response?.data?.error?.message || 'Bölge güncellenirken bir hata oluştu';
      setToast({ message, type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600">Bölgeler yükleniyor...</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <Card>
        <p className="text-gray-600">Restoran bulunamadı.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Teslimat Bölgeleri</h2>
          <p className="text-sm text-gray-600 mt-1">
            Restoranınız için özel teslimat bölgeleri tanımlayın. Her bölge için farklı teslimat ücreti ve minimum sipariş tutarı belirleyebilirsiniz.
          </p>
        </div>
        {!showCreateForm && (
          <Button
            variant="primary"
            onClick={() => setShowCreateForm(true)}
            disabled={zones.length >= 10}
          >
            Yeni Bölge Ekle
          </Button>
        )}
      </div>

      {zones.length >= 10 && (
        <Card className="bg-yellow-50 border-yellow-200">
          <p className="text-yellow-800 text-sm">
            Maksimum 10 bölge tanımlayabilirsiniz. Yeni bölge eklemek için mevcut bir bölgeyi silin.
          </p>
        </Card>
      )}

      {showCreateForm && (
        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {editingZone ? 'Teslimat Bölgesini Düzenle' : 'Yeni Teslimat Bölgesi'}
          </h3>

          <div className="space-y-4">
            <Input
              label="Bölge Adı *"
              placeholder="Örn: Merkez Bölge, Kuzey Bölge"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />

            <div className="grid grid-cols-3 gap-4">
              <NumberInput
                label="Teslimat Ücreti (TRY) *"
                value={formData.deliveryFee === 0 ? '' : String(formData.deliveryFee)}
                onChange={(s) =>
                  setFormData({ ...formData, deliveryFee: s === '' ? 0 : parseFloat(s) || 0 })
                }
              />
              <NumberInput
                label="Minimum Sipariş (TRY) *"
                value={formData.minimumOrderAmount === 0 ? '' : String(formData.minimumOrderAmount)}
                onChange={(s) =>
                  setFormData({
                    ...formData,
                    minimumOrderAmount: s === '' ? 0 : parseFloat(s) || 0,
                  })
                }
              />
              <NumberInput
                label="Tahmini Süre (dakika) *"
                integerOnly
                value={formData.estimatedDeliveryMinutes === 0 ? '' : String(formData.estimatedDeliveryMinutes)}
                onChange={(s) =>
                  setFormData({
                    ...formData,
                    estimatedDeliveryMinutes: s === '' ? 0 : parseInt(s, 10) || 0,
                  })
                }
              />
            </div>

            <NumberInput
              label="Öncelik (yüksek = önce seçilir)"
              integerOnly
              value={formData.priority === 0 ? '' : String(formData.priority)}
              onChange={(s) =>
                setFormData({ ...formData, priority: s === '' ? 0 : parseInt(s, 10) || 0 })
              }
            />

            {restaurant.latitude && restaurant.longitude ? (
              <ZoneDrawingMap
                key={editingZone ? `edit-${editingZone.id}` : 'create'}
                restaurantLat={restaurant.latitude}
                restaurantLng={restaurant.longitude}
                initialPolygon={polygon}
                onPolygonChange={(poly) => {
                  console.log('[DeliveryZonesTab] Polygon changed:', poly);
                  setPolygon(poly);
                }}
                onValidationChange={(valid, error) => {
                  console.log('[DeliveryZonesTab] Validation changed:', { valid, error, currentState: { isPolygonValid, polygon: !!polygon, name: formData.name } });
                  setIsPolygonValid(valid);
                  setValidationError(error || null);
                }}
              />
            ) : (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  Restoran konumu tanımlı değil. Lütfen önce restoran ayarlarından konum bilgisini ekleyin.
                </p>
              </div>
            )}

            {!isPolygonValid && validationError && (
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-amber-900 mb-2">⚠️ Dikkat: Polygon geçerli değil</p>
                    <p className="text-sm text-amber-800 mb-2">{validationError}</p>
                    {validationError?.includes('restoran') && (
                      <div className="mt-3 p-3 bg-white rounded border border-amber-200">
                        <p className="text-xs font-semibold text-amber-900 mb-2">💡 Nasıl düzeltebilirim?</p>
                        <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                          <li>Haritadaki <strong className="text-green-700">yeşil marker</strong> restoran konumunuzdur</li>
                          <li>"Düzenle" butonuna tıklayın</li>
                          <li>Noktaları sürükleyerek polygon'u genişletin</li>
                          <li>Yeşil marker polygon içinde kalmalı</li>
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {(!isPolygonValid || !formData.name || !formData.estimatedDeliveryMinutes) && (
                <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                  <p className="font-medium mb-1">Buton neden pasif?</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {!formData.name && <li>Bölge adı girilmedi</li>}
                    {!formData.estimatedDeliveryMinutes && <li>Tahmini teslimat süresi girilmedi</li>}
                    {!isPolygonValid && <li>Polygon geçerli değil</li>}
                    {!polygon && <li>Polygon çizilmedi</li>}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={editingZone ? handleUpdateZone : handleCreateZone}
                  disabled={
                    !isPolygonValid ||
                    !formData.name ||
                    !formData.estimatedDeliveryMinutes ||
                    formData.estimatedDeliveryMinutes < 1 ||
                    isCreating ||
                    isUpdating
                  }
                  isLoading={isCreating || isUpdating}
                >
                  {editingZone ? 'Bölgeyi Güncelle' : 'Bölgeyi Kaydet'}
                </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingZone(null);
                  setFormData({
                    name: '',
                    deliveryFee: 0,
                    minimumOrderAmount: 0,
                    estimatedDeliveryMinutes: 30,
                    priority: 0,
                  });
                  setPolygon(null);
                  setIsPolygonValid(false);
                  setValidationError(null);
                }}
              >
                İptal
              </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4">
        {zones.map((zone) => (
          <Card key={zone.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-gray-900">{zone.name}</h3>
                  <Badge variant={zone.isActive ? 'success' : 'default'}>
                    {zone.isActive ? 'Aktif' : 'Pasif'}
                  </Badge>
                  {zone.priority > 0 && (
                    <Badge variant="info">Öncelik: {zone.priority}</Badge>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Teslimat Ücreti:</span>
                    <p className="font-semibold text-gray-900">{zone.deliveryFee} TRY</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Min. Sipariş:</span>
                    <p className="font-semibold text-gray-900">{zone.minimumOrderAmount} TRY</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Tahmini Süre:</span>
                    <p className="font-semibold text-gray-900">{zone.estimatedDeliveryMinutes} dk</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditZone(zone)}
                >
                  Düzenle
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteZoneId(zone.id)}
                >
                  Sil
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {zones.length === 0 && !showCreateForm && (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Henüz teslimat bölgesi tanımlanmamış.</p>
              <Button variant="primary" onClick={() => setShowCreateForm(true)}>
                İlk Bölgeyi Oluştur
              </Button>
            </div>
          </Card>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteZoneId}
        title="Bölgeyi Sil"
        description={`"${zones.find(z => z.id === deleteZoneId)?.name || ''}" adlı teslimat bölgesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Evet, Sil"
        cancelText="Vazgeç"
        variant="danger"
        isConfirmLoading={isDeleting}
        onConfirm={handleDeleteZone}
        onClose={() => setDeleteZoneId(null)}
      />
    </div>
  );
};

export default DeliveryZonesTab;
